import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, BookmarkPlus, MessageSquare, Copy, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCatalogue } from "@/context/CatalogueContext";
import { api, errorMessage } from "@/lib/api";
import { copyText, targetName, targetPath } from "@/lib/catalogue";
import { formatDateTime } from "@/lib/format";

const HEADER = "Campaign | Agent | Service line | Audience stream | Region | Review verdict | What the human does next";

export const WhatNext = ({ fromId, onSavePrompt }) => {
  const { next_steps: steps, agentsByKey, workflowsByKey } = useCatalogue();
  const mine = steps.filter((s) => s.from_id === fromId);
  const copyHeader = async () => {
    if (await copyText(HEADER)) toast.success("Delivery header copied");
    else toast.error("Could not copy. Select the text manually.");
  };
  return (
    <section className="rounded-xl border bg-card p-5" data-testid="what-next">
      <h2 className="text-lg font-semibold">What next</h2>
      {mine.length > 0 && (
        <ul className="mt-3 grid gap-2 md:grid-cols-2">
          {mine.flatMap((s) => s.to_ids.map((to) => (
            <li key={`${s.id}-${to}`}>
              <Link to={targetPath(to, workflowsByKey)} className="flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm transition-quick hover:bg-muted" data-testid={`what-next-link-${to}`}>
                <span><span className="font-medium">{s.label}</span> <span className="text-muted-foreground">with {targetName(to, agentsByKey, workflowsByKey)}</span></span>
                <ArrowRight className="!h-4 !w-4 shrink-0" aria-hidden="true" />
              </Link>
            </li>
          )))}
        </ul>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" onClick={onSavePrompt} data-testid="what-next-save-prompt"><BookmarkPlus className="!h-4 !w-4" /> Save a prompt</Button>
        <Button variant="secondary" size="sm" asChild><Link to={`/feedback?about=${fromId}`} data-testid="what-next-feedback"><MessageSquare className="!h-4 !w-4" /> Give feedback</Link></Button>
        <Button variant="secondary" size="sm" onClick={copyHeader} data-testid="what-next-copy-header"><Copy className="!h-4 !w-4" /> Copy the delivery header format</Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{HEADER}</p>
    </section>
  );
};

export const RunRow = ({ run, onChanged, openPath = "", subtitle }) => {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(run.label);
  const formDone = run.status === "finished" && run.mode !== "chat";
  const open = () => {
    if (!formDone) return navigate(`${openPath}?run=${run.id}`);
    if (openPath) navigate(openPath);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const save = async () => {
    setEditing(false);
    if (!label.trim() || label === run.label) return setLabel(run.label);
    try { await api.patch(`/runs/${run.id}`, { label }); onChanged(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <li className="flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-sm" data-testid={`run-row-${run.id}`}>
      <div className="min-w-0 flex-1">
        {editing ? (
          <Input autoFocus value={label} maxLength={120} onChange={(e) => setLabel(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && save()} aria-label="Run label" className="h-8" data-testid="run-label-input" />
        ) : (
          <button type="button" onClick={() => setEditing(true)} className="flex items-center gap-2 font-medium hover:underline" data-testid="run-label-edit"><span className="truncate">{run.label}</span><Pencil className="!h-3.5 !w-3.5 text-muted-foreground" aria-hidden="true" /></button>
        )}
        <p className="text-xs text-muted-foreground">{subtitle && `${subtitle} · `}{formatDateTime(run.started_at)} · {run.status} · {run.campaign || "No campaign"}{run.gate_ticks?.length ? ` · ${run.gate_ticks.length} gates passed` : ""}</p>
      </div>
      <div className="flex flex-col items-end gap-0.5">
        <Button variant="ghost" size="sm" onClick={open} data-testid={`run-open-${run.id}`}>Open</Button>
        {formDone && <p className="text-xs text-muted-foreground" data-testid={`run-open-hint-${run.id}`}>Your result is in the form's history (clock icon, top left).</p>}
      </div>
    </li>
  );
};

export const RunsPanel = ({ targetId, refreshKey }) => {
  const [runs, setRuns] = useState(null);
  const load = () => api.get("/runs", { params: { target_id: targetId, limit: 5 } }).then(({ data }) => setRuns(data)).catch(() => setRuns([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [targetId, refreshKey]);
  return (
    <section data-testid="runs-panel">
      <h2 className="mb-3 text-lg font-semibold">Your runs here</h2>
      {runs && runs.length === 0 && <p className="text-sm text-muted-foreground">No runs yet. Opening the run surface starts one.</p>}
      <ul className="space-y-2">{(runs || []).map((r) => <RunRow key={r.id} run={r} onChanged={load} />)}</ul>
      <p className="mt-2 text-xs text-muted-foreground">Only you can see these. The conversation and its output stay in the Agentic Brain's own history.</p>
    </section>
  );
};
